import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Shield, FileText, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalPage = () => {
  const { schoolCode, pageType } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageIcons = {
    'privacy-policy': <Shield className="h-6 w-6" />,
    'terms-of-use': <FileText className="h-6 w-6" />,
    'refund-policy': <RefreshCw className="h-6 w-6" />
  };

  const pageTypeMap = {
    'privacy-policy': 'privacy_policy',
    'terms-of-use': 'terms_of_use',
    'refund-policy': 'refund_policy'
  };

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const apiPageType = pageTypeMap[pageType] || pageType;
        const res = await axios.get(`${API_URL}/api/legal-pages/school-by-code/${schoolCode}/${apiPageType}`);
        setPageData(res.data);
      } catch (err) {
        console.error('Failed to load page:', err);
        setError('Failed to load page content');
      } finally {
        setLoading(false);
      }
    };

    if (schoolCode && pageType) {
      fetchPage();
    }
  }, [schoolCode, pageType]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.history.back()}>Go Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => window.history.back()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            {pageIcons[pageType]}
            <h1 className="text-xl font-semibold">{pageData?.title}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
          {pageData?.school_name && (
            <p className="text-sm text-gray-500 mb-4">
              {pageData.school_name}
            </p>
          )}
          
          <div className="prose prose-blue max-w-none">
            <ReactMarkdown>{pageData?.content || ''}</ReactMarkdown>
          </div>

          {pageData?.updated_at && (
            <p className="text-xs text-gray-400 mt-8 pt-4 border-t">
              Last updated: {new Date(pageData.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-8 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Powered by <a href="https://cloudcampus.co.in" className="text-blue-600 hover:underline">Cloud Campus</a></p>
          <p className="mt-1">© {new Date().getFullYear()} Ncriptech Global Private Limited</p>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
